import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, CheckCircle2, ChevronDown, FileCheck2, ShieldCheck, Upload } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuthState } from '@/components/feature/AuthGuard';
import type { MemberPolicyDocument } from '@/pages/og-qi-healing/lib/types';

type PhotoMode = 'none' | 'upload' | 'camera';

const DRAFT_KEY = 'aum-member-onboarding-draft-v1';

interface OnboardingDraft {
  accepted: Record<number, boolean>;
  expanded: Record<number, boolean>;
  documentsOpen: boolean;
}

interface CompletionProfile {
  membership_status: 'pending' | 'active' | 'suspended' | 'terminated';
  onboarding_completed_at: string | null;
  profile_photo_uploaded_at: string | null;
  photo_storage_consent: boolean;
  ahai_photo_analysis_consent: boolean;
  mentor_photo_access_consent: boolean;
  updated_at: string;
}

interface IdentityDocumentStatus {
  document_type: string;
  uploaded_at: string;
  review_status: string | null;
}

const FALLBACK_POLICIES: MemberPolicyDocument[] = [
  {
    id: -1,
    policy_key: 'participation_rules',
    version: '2026-09-22',
    title: 'AUM 系統參與規範',
    body_markdown: '參與者須遵守AUM宇宙生命樹三部曲所展開並依法更新的管理規則；不得冒用、未經授權大量擷取、破解、干擾系統或侵害其他參與者權益。重大違規時，系統得依參與規範暫停或終止資格；停權、終止、已付款項及退款，依當時有效契約、違規事實及適用法律辦理。',
    is_required: true,
    effective_at: '2026-09-22T00:00:00.000Z',
  },
  {
    id: -2,
    policy_key: 'privacy_and_case_use',
    version: '2026-09-22',
    title: '個資、案例研究與公開規則',
    body_markdown: '系統依告知目的保存療育與追蹤紀錄。案主可一次授權去識別化案例研究與匿名文字公開；同一主訴後續文字紀錄不必逐次同意。照片、聲音與影片仍須逐件取得公開同意。公開資料不得包含姓名、地址、聯絡方式或足以辨識本人之資訊。',
    is_required: true,
    effective_at: '2026-09-22T00:00:00.000Z',
  },
];

function safeFileName(name: string): string {
  return name.normalize('NFKC').replace(/[^a-zA-Z0-9._-]+/g, '-').slice(-100);
}

export default function MemberOnboardingPage() {
  const auth = useAuthState();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState<MemberPolicyDocument[]>(FALLBACK_POLICIES);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [accepted, setAccepted] = useState<Record<number, boolean>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [photoMode, setPhotoMode] = useState<PhotoMode>('none');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoStorageConsent, setPhotoStorageConsent] = useState(false);
  const [ahaiConsent, setAhaiConsent] = useState(false);
  const [mentorAccessConsent, setMentorAccessConsent] = useState(false);
  const [identityFiles, setIdentityFiles] = useState<Record<string, File | null>>({
    national_id_front: null,
    national_id_back: null,
    passport: null,
    address_proof: null,
    tax_document: null,
  });
  const [identityCrossChecked, setIdentityCrossChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [completionProfile, setCompletionProfile] = useState<CompletionProfile | null>(null);
  const [identityDocumentStatuses, setIdentityDocumentStatuses] = useState<IdentityDocumentStatus[]>([]);
  const [completionLoading, setCompletionLoading] = useState(true);
  const [completionReadError, setCompletionReadError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as Partial<OnboardingDraft>;
      if (draft.accepted) setAccepted(draft.accepted);
      if (draft.expanded) setExpanded(draft.expanded);
      if (typeof draft.documentsOpen === 'boolean') setDocumentsOpen(draft.documentsOpen);
    } catch {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, []);

  useEffect(() => {
    if (auth.loading || auth.user) return;
    const timer = window.setTimeout(() => {
      navigate('/account?returnTo=%2Fmember%2Fonboarding', { replace: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [auth.loading, auth.user, navigate]);

  useEffect(() => {
    const draft: OnboardingDraft = { accepted, expanded, documentsOpen };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [accepted, expanded, documentsOpen]);

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) {
      setCompletionLoading(false);
      return;
    }

    let cancelled = false;
    setCompletionLoading(true);
    setCompletionReadError(null);
    Promise.all([
      supabase
        .from('member_onboarding_profiles')
        .select('membership_status,onboarding_completed_at,profile_photo_uploaded_at,photo_storage_consent,ahai_photo_analysis_consent,mentor_photo_access_consent,updated_at')
        .eq('user_id', auth.user.id)
        .maybeSingle(),
      supabase
        .from('identity_documents')
        .select('document_type,uploaded_at,review_status')
        .eq('user_id', auth.user.id)
        .order('uploaded_at', { ascending: false }),
    ]).then(([profileResult, identityResult]) => {
      if (cancelled) return;
      if (!profileResult.error && profileResult.data) {
        setCompletionProfile(profileResult.data as CompletionProfile);
      } else if (profileResult.error) {
        setCompletionReadError('目前無法讀取會員啟用狀態，請重新整理；若仍出現此訊息，請聯絡管理者。');
      }
      if (!identityResult.error && identityResult.data) {
        setIdentityDocumentStatuses(identityResult.data as IdentityDocumentStatus[]);
      }
      setCompletionLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setCompletionReadError('目前無法讀取會員啟用狀態，請重新整理；若仍出現此訊息，請聯絡管理者。');
      setCompletionLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [auth.loading, auth.user]);

  useEffect(() => {
    supabase
      .from('member_policy_documents')
      .select('*')
      .is('retired_at', null)
      .order('effective_at')
      .then(({ data, error }) => {
        if (error) return;
        if (data && data.length > 0) setPolicies(data as MemberPolicyDocument[]);
      });
  }, []);

  const requiredAccepted = useMemo(
    () => policies.filter((item) => item.is_required).every((item) => accepted[item.id]),
    [accepted, policies],
  );

  const handlePhoto = (file: File | null, mode: PhotoMode) => {
    setPhoto(file);
    setPhotoMode(file ? mode : 'none');
    if (!file) {
      setPhotoStorageConsent(false);
      setAhaiConsent(false);
      setMentorAccessConsent(false);
    }
  };

  const submit = async () => {
    setMessage(null);
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session?.user) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ accepted, expanded, documentsOpen }));
      navigate('/account?returnTo=%2Fmember%2Fonboarding');
      return;
    }
    if (!requiredAccepted) {
      setMessage('請先展開閱讀並勾選兩份必要文件。');
      return;
    }
    if (photo && (!photoStorageConsent || !ahaiConsent || !mentorAccessConsent)) {
      setMessage('選擇大頭照後，三項同意皆為必選，請全部勾選。');
      return;
    }
    if (!identityFiles.national_id_front || !identityFiles.national_id_back || !identityFiles.passport) {
      setMessage('請完整上傳身分證正面、身分證反面與護照。');
      return;
    }
    if (!identityCrossChecked) {
      setMessage('請確認身分證正反面與護照均屬同一人。');
      return;
    }

    setSaving(true);
    try {
      const uid = sessionData.session.user.id;
      const now = new Date().toISOString();

      for (const policy of policies.filter((item) => accepted[item.id])) {
        const { error } = await supabase.from('member_policy_acceptances').upsert(
          {
            user_id: uid,
            policy_document_id: policy.id,
            accepted_at: now,
            acceptance_source: 'web',
            device_info: {
              browser: navigator.userAgent,
              language: navigator.language,
            },
          },
          { onConflict: 'user_id,policy_document_id' },
        );
        if (error) throw error;
      }

      let photoPath: string | null = null;
      if (photo) {
        photoPath = `${uid}/${Date.now()}-${safeFileName(photo.name || 'profile-photo.jpg')}`;
        const { error } = await supabase.storage.from('member-profile-photos').upload(photoPath, photo, {
          upsert: false,
          contentType: photo.type || 'image/jpeg',
        });
        if (error) throw error;
      }

      const { error: profileError } = await supabase.from('member_onboarding_profiles').upsert({
        user_id: uid,
        membership_status: 'active',
        profile_photo_path: photoPath,
        profile_photo_uploaded_at: photo ? now : null,
        profile_photo_source: photo ? photoMode : null,
        photo_storage_consent: Boolean(photo && photoStorageConsent),
        ahai_photo_analysis_consent: Boolean(photo && ahaiConsent),
        mentor_photo_access_consent: Boolean(photo && mentorAccessConsent),
        photo_consent_version: photo ? '2026-09-22' : null,
        photo_consented_at: photo ? now : null,
        onboarding_completed_at: now,
        updated_at: now,
      });
      if (profileError) throw profileError;

      for (const [documentType, identityFile] of Object.entries(identityFiles)) {
        if (!identityFile) continue;
        const documentPath = `${uid}/${Date.now()}-${documentType}-${safeFileName(identityFile.name)}`;
        const { error: uploadError } = await supabase.storage
          .from('identity-documents')
          .upload(documentPath, identityFile, { upsert: false, contentType: identityFile.type });
        if (uploadError) throw uploadError;

        const { error: documentError } = await supabase.from('identity_documents').insert({
          user_id: uid,
          document_type: documentType,
          storage_path: documentPath,
          uploaded_by: uid,
          uploaded_at: now,
        });
        if (documentError) throw documentError;
      }

      localStorage.removeItem(DRAFT_KEY);
      setCompletionProfile({
        membership_status: 'active',
        onboarding_completed_at: now,
        profile_photo_uploaded_at: photo ? now : null,
        photo_storage_consent: Boolean(photo && photoStorageConsent),
        ahai_photo_analysis_consent: Boolean(photo && ahaiConsent),
        mentor_photo_access_consent: Boolean(photo && mentorAccessConsent),
        updated_at: now,
      });
      setIdentityDocumentStatuses(
        Object.entries(identityFiles)
          .filter(([, file]) => Boolean(file))
          .map(([documentType]) => ({
            document_type: documentType,
            uploaded_at: now,
            review_status: 'pending',
          })),
      );
      setMessage('正式會員資料已完成。AI Angel 可依您同意提供的資料開始個人化引導。');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '儲存失敗，請稍後再試。');
    } finally {
      setSaving(false);
    }
  };

  if (auth.loading || !auth.user || completionLoading) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-20 px-4">
        <div className="max-w-lg mx-auto rounded-2xl border bg-white p-8 text-center space-y-4">
          <div className="w-10 h-10 mx-auto border-2 border-amber-300 border-t-amber-700 rounded-full animate-spin" />
          <h1 className="text-xl font-semibold">請先登入 AUM1001 帳號</h1>
          <p className="text-sm text-foreground-600">正在前往登入頁；登入完成後會自動回到本頁繼續。</p>
          <Link
            to="/account?returnTo=%2Fmember%2Fonboarding"
            className="inline-flex rounded-lg bg-amber-700 px-5 py-2.5 text-white font-medium"
          >
            前往登入
          </Link>
        </div>
      </main>
    );
  }

  if (completionProfile && (completionProfile.membership_status === 'active' || completionProfile.onboarding_completed_at)) {
    const completedAt = new Date(completionProfile.onboarding_completed_at || completionProfile.updated_at).toLocaleString('zh-TW', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const statusLabels: Record<CompletionProfile['membership_status'], string> = {
      pending: '資料已送出，待確認',
      active: '正式會員有效',
      suspended: '資格暫停',
      terminated: '資格已終止',
    };
    const documentLabels: Record<string, string> = {
      national_id_front: '身分證正面',
      national_id_back: '身分證反面',
      passport: '護照',
      address_proof: '住址證明',
      tax_document: '稅務／收款證明',
    };
    const latestDocuments = Object.values(
      identityDocumentStatuses.reduce<Record<string, IdentityDocumentStatus>>((latest, item) => {
        if (!latest[item.document_type]) latest[item.document_type] = item;
        return latest;
      }, {}),
    );

    return (
      <main className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <header className="rounded-2xl border border-emerald-200 bg-white p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-12 h-12 shrink-0 text-emerald-600" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-emerald-700">{statusLabels[completionProfile.membership_status]}</p>
                <h1 className="mt-1 text-3xl font-bold text-foreground-900">已完成正式會員啟用</h1>
                <p className="mt-2 text-foreground-600">完成時間：{completedAt}</p>
              </div>
            </div>
          </header>

          {completionProfile.membership_status !== 'active' && (
            <div role="status" className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              目前狀態：{statusLabels[completionProfile.membership_status]}。如需協助，請由帳號頁聯絡管理者。
            </div>
          )}

          <section className="rounded-2xl border bg-white p-5">
            <h2 className="text-xl font-semibold">會員啟用項目</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                ['必要文件', '已閱讀並同意'],
                ['大頭照', completionProfile.profile_photo_uploaded_at ? '已提供' : '未提供（自願）'],
                ['AHAi 個人化分析', completionProfile.ahai_photo_analysis_consent ? '已同意' : '未授權'],
                ['指定 Mentor 查看', completionProfile.mentor_photo_access_consent ? '已同意' : '未授權'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border bg-foreground-50 p-4">
                  <p className="text-sm text-foreground-500">{label}</p>
                  <p className="mt-1 font-medium text-foreground-900">{value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border bg-white p-5">
            <h2 className="text-xl font-semibold">身分文件狀態</h2>
            {latestDocuments.length > 0 ? (
              <div className="mt-4 divide-y">
                {latestDocuments.map((item) => (
                  <div key={item.document_type} className="flex items-center justify-between gap-4 py-3">
                    <span>{documentLabels[item.document_type] || item.document_type}</span>
                    <span className="text-sm text-emerald-700">已安全上傳</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-foreground-600">已完成會員啟用；文件狀態將由系統安全記錄。</p>
            )}
          </section>

          <div className="grid gap-3 sm:grid-cols-2">
            <Link to="/og/portal" className="rounded-xl bg-amber-700 px-5 py-3 text-center font-semibold text-white">
              進入 OG 一之門
            </Link>
            <Link to="/account" className="rounded-xl border bg-white px-5 py-3 text-center font-semibold text-foreground-800">
              返回帳號
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <Link to="/account" className="text-sm text-amber-800 hover:underline">← 返回帳號</Link>
          <h1 className="text-3xl font-bold mt-3 text-foreground-900">正式會員啟用</h1>
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 leading-7 text-foreground-800">
  這是您有機會參與地球人在行星地球「實質升維」之全球教學、研究與踐行的新時空。為保護您的權益與隱私，首次註冊請提供以下真實資料。
          </p>

          <p className="mt-2 text-foreground-600">
            AI Angel 將帶您完成必要規則、身分資料與自願提供的大頭照設定。
          </p>
        </header>

        <section className="bg-white border rounded-2xl p-5 space-y-4">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 text-left"
            aria-expanded={documentsOpen}
            onClick={() => setDocumentsOpen((open) => !open)}
          >
            <span className="flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-amber-700" />
              <span className="text-xl font-semibold">一、閱讀兩份必要文件</span>
            </span>
            <span className="flex items-center gap-2 text-sm text-amber-800">
              {documentsOpen ? '收合' : '點此展開'}
              <ChevronDown className={`w-5 h-5 transition-transform ${documentsOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {documentsOpen && policies.map((policy) => (
            <article key={policy.id} className="border rounded-xl p-4">
              <button
                type="button"
                className="w-full flex justify-between text-left font-medium"
                onClick={() => setExpanded((old) => ({ ...old, [policy.id]: !old[policy.id] }))}
              >
                <span>{policy.title}（{policy.version}）</span>
                <span>{expanded[policy.id] ? '收合' : '展開閱讀'}</span>
              </button>
              {expanded[policy.id] && (
                <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground-700 bg-foreground-50 rounded-lg p-4">
                  {policy.body_markdown}
                </div>
              )}
              <label className="flex items-start gap-3 mt-4">
                <input
                  type="checkbox"
                  checked={Boolean(accepted[policy.id])}
                  disabled={!expanded[policy.id]}
                  onChange={(event) => setAccepted((old) => ({ ...old, [policy.id]: event.target.checked }))}
                  className="mt-1"
                />
                <span className="text-sm">我已閱讀並同意本版本內容</span>
              </label>
            </article>
          ))}
        </section>

        <section className="bg-white border rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-amber-700" />
            <h2 className="text-xl font-semibold">二、大頭照與AHAi輔助分析（自願）</h2>
          </div>
          <p className="text-sm text-foreground-600">
            不提供照片不影響一般會員資格。照片分析僅作個人化引導參考，不代表醫療、心理、人格或維度診斷。
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="border-2 border-amber-500 bg-amber-50 rounded-xl p-4 cursor-pointer">
              <Camera className="w-5 h-5 mb-2 text-amber-700" />
              <span className="font-semibold">現場拍照（建議首選）</span>
              <p className="mt-1 text-xs text-foreground-600">最能反映目前狀態，適合AHAi當次個人化參考。</p>
              <input type="file" accept="image/*" capture="user" className="block mt-3 text-sm"
                onChange={(event) => handlePhoto(event.target.files?.[0] || null, 'camera')} />
            </label>
            <label className="border rounded-xl p-4 cursor-pointer">
              <Upload className="w-5 h-5 mb-2" />
              <span className="font-semibold">上傳既有照片（補充）</span>
              <p className="mt-1 text-xs text-foreground-600">照片有時間差時，可能無法準確反映現況；請優先使用近期、正面、自然光照片。</p>
              <input type="file" accept="image/*" className="block mt-3 text-sm"
                onChange={(event) => handlePhoto(event.target.files?.[0] || null, 'upload')} />
            </label>
          </div>
          {photo && (
            <div className="space-y-2 text-sm">
              <p>已選擇：{photo.name || '現場照片'}</p>
              <p className="font-medium text-amber-900">選擇照片後，以下三項皆為必選：</p>
              <label className="block"><input type="checkbox" className="mr-2" checked={photoStorageConsent}
                onChange={(e) => setPhotoStorageConsent(e.target.checked)} />同意保存這張大頭照 <span className="text-red-700">必選</span></label>
              <label className="block"><input type="checkbox" className="mr-2" checked={ahaiConsent}
                onChange={(e) => setAhaiConsent(e.target.checked)} />同意用於AHAi個人化輔助分析 <span className="text-red-700">必選</span></label>
              <label className="block"><input type="checkbox" className="mr-2" checked={mentorAccessConsent}
                onChange={(e) => setMentorAccessConsent(e.target.checked)} />同意指定Mentor查看 <span className="text-red-700">必選</span></label>
            </div>
          )}
        </section>

        <section className="bg-white border rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-700" />
            <h2 className="text-xl font-semibold">三、身分證明（依角色需要）</h2>
          </div>
          <p className="text-sm text-foreground-600">
            身分證正反面與護照構成雙證件勾稽，三個檔案皆須上傳；住址及稅務／收款證明可選填。所有文件儲存在私人空間，並記錄上傳時間。
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ['national_id_front', '身分證正面', true],
              ['national_id_back', '身分證反面', true],
              ['passport', '護照', true],
              ['address_proof', '住址證明', false],
              ['tax_document', '稅務／收款證明', false],
            ].map(([type, label, required]) => (
              <label key={String(type)} className="border rounded-xl p-4">
                <span className="font-medium">{label}</span>
                <span className={`ml-2 text-xs ${required ? 'text-red-700' : 'text-foreground-500'}`}>
                  {required ? '必須' : '選填'}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  required={Boolean(required)}
                  className="block mt-3 text-sm w-full"
                  onChange={(event) => setIdentityFiles((old) => ({
                    ...old,
                    [String(type)]: event.target.files?.[0] || null,
                  }))}
                />
              </label>
            ))}
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <input
              type="checkbox"
              className="mt-1"
              checked={identityCrossChecked}
              onChange={(event) => setIdentityCrossChecked(event.target.checked)}
            />
            <span className="text-sm">
              我已確認身分證正反面與護照均屬同一人，資料一致，可供雙證件勾稽。
              <span className="ml-2 text-red-700">必選</span>
            </span>
          </label>
        </section>

        {completionReadError && (
          <div role="alert" className="border border-red-300 bg-red-50 text-red-900 rounded-xl p-4">
            {completionReadError}
          </div>
        )}
        {message && <div role="status" className="border border-amber-300 bg-amber-50 rounded-xl p-4">{message}</div>}

        <button type="button" onClick={submit} disabled={saving || policies.length === 0}
          className="w-full rounded-xl bg-amber-700 text-white font-semibold py-3 disabled:opacity-50">
          {saving ? '儲存中…' : '完成並啟用正式會員'}
        </button>
      </div>
    </main>
  );
}
